import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LogOut,
    Save,
    Home as HomeIcon,
    BookOpen,
    Brain,
    Sun,
    Users,
    Image as ImageIcon,
    Phone,
    Settings as SettingsIcon,
    MessageSquare,
    Globe,
    Loader2,
    KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useSite } from "../context/SiteContext";
import { setAdminSession, isAdminLoggedIn, setAdminPwd } from "../lib/store";
import {
    TextField,
    TextAreaField,
    ImageField,
    ListEditor,
    Field,
} from "../admin/Fields";
import {
    getFirebaseConfig,
    saveFirebaseConfig,
    clearFirebaseConfig,
    firebaseReady,
} from "../lib/firebase";
import { getR2Config, saveR2Config, r2Ready } from "../lib/r2";

const TABS = [
    { value: "branding", label: "Brend", icon: ImageIcon },
    { value: "hero", label: "Ana ekran", icon: HomeIcon },
    { value: "pillar", label: "Psixoloq", icon: Brain },
    { value: "courses", label: "Kurslar", icon: BookOpen },
    { value: "summer", label: "Yay", icon: Sun },
    { value: "teachers", label: "Müəllimlər", icon: Users },
    { value: "gallery", label: "Qalereya", icon: ImageIcon },
    { value: "reviews", label: "Rəylər", icon: MessageSquare },
    { value: "contact", label: "Əlaqə", icon: Phone },
    { value: "seo", label: "SEO", icon: Globe },
    { value: "settings", label: "Tənzimləmələr", icon: SettingsIcon },
];

const Card = ({ title, children }) => (
    <section className="bg-slate-100/60 border border-slate-200 rounded-xl p-6 space-y-5">
        <h2 className="font-serif-display text-2xl text-[#0A1F3D]">{title}</h2>
        <div className="space-y-5">{children}</div>
    </section>
);

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { content, replace, loading } = useSite();
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [active, setActive] = useState("branding");

    useEffect(() => {
        if (!isAdminLoggedIn()) navigate("/admin");
    }, [navigate]);

    useEffect(() => {
        if (!loading && content) setDraft(content);
    }, [content, loading]);

    const setKey = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
    const patchKey = (key, patch) =>
        setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

    const save = async () => {
        if (!draft) return;
        setSaving(true);
        try {
            await replace(draft);
            toast.success("Dəyişikliklər yadda saxlanıldı");
        } catch (e) {
            console.error(e);
            toast.error(
                "Saxlanılarkən xəta. Firebase konfiqurasiyasını yoxlayın."
            );
        } finally {
            setSaving(false);
        }
    };

    const logout = () => {
        setAdminSession(false);
        navigate("/admin");
    };

    if (loading || !draft) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">
                <Loader2 className="animate-spin" />
            </div>
        );
    }

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-slate-50 text-slate-900"
        >
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                        <img
                            src={content.branding?.logoUrl}
                            alt=""
                            className="h-8 w-8"
                        />
                        <div>
                            <div className="font-serif-display text-lg leading-none">
                                KA Academy
                            </div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-1">
                                Admin Panel
                            </div>
                        </div>
                        <div className="ml-4 flex items-center gap-2 text-xs">
                            <span
                                className={`inline-block w-2 h-2 rounded-full ${firebaseReady() ? "bg-emerald-500" : "bg-amber-500"}`}
                            />
                            <span className="text-slate-600">
                                Firebase:{" "}
                                {firebaseReady() ? "qoşulu" : "lokal"}
                            </span>
                            <span className="text-slate-300 mx-2">•</span>
                            <span
                                className={`inline-block w-2 h-2 rounded-full ${r2Ready() ? "bg-emerald-500" : "bg-amber-500"}`}
                            />
                            <span className="text-slate-600">
                                R2: {r2Ready() ? "qoşulu" : "fallback"}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid="admin-save-button"
                            className="bg-[#0A1F3D] hover:bg-[#061632] text-white"
                        >
                            {saving ? (
                                <Loader2 size={14} className="mr-2 animate-spin" />
                            ) : (
                                <Save size={14} className="mr-2" />
                            )}
                            Yadda saxla
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => window.open("/", "_blank")}
                            data-testid="admin-view-site"
                        >
                            Saytı bax
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={logout}
                            data-testid="admin-logout"
                        >
                            <LogOut size={14} className="mr-2" />
                            Çıxış
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 py-8">
                <Tabs value={active} onValueChange={setActive}>
                    <TabsList className="bg-white border border-slate-200 p-1 grid grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-1 h-auto rounded-xl">
                        {TABS.map((t) => (
                            <TabsTrigger
                                key={t.value}
                                value={t.value}
                                data-testid={`tab-${t.value}`}
                                className="flex items-center gap-2 data-[state=active]:bg-[#0A1F3D] data-[state=active]:text-white py-2 rounded-lg"
                            >
                                <t.icon size={14} />
                                <span className="hidden md:inline text-xs">
                                    {t.label}
                                </span>
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <div className="mt-6">
                        <TabsContent value="branding" className="space-y-6">
                            <Card title="Brend və sosial">
                                <TextField
                                    label="Sayt adı"
                                    value={draft.branding?.name}
                                    onChange={(v) => patchKey("branding", { name: v })}
                                    testId="branding-name"
                                />
                                <TextField
                                    label="Şüar"
                                    value={draft.branding?.tagline}
                                    onChange={(v) =>
                                        patchKey("branding", { tagline: v })
                                    }
                                    testId="branding-tagline"
                                />
                                <ImageField
                                    label="Loqo"
                                    value={draft.branding?.logoUrl}
                                    onChange={(v) =>
                                        patchKey("branding", { logoUrl: v })
                                    }
                                    testId="branding-logo"
                                />
                            </Card>
                            <Card title="Sosial şəbəkə və WhatsApp">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <TextField
                                        label="WhatsApp nömrəsi (rəqəmlər)"
                                        value={draft.social?.whatsapp}
                                        onChange={(v) =>
                                            patchKey("social", { whatsapp: v })
                                        }
                                        hint="Beynəlxalq format, məs: 994703940304"
                                        testId="social-whatsapp"
                                    />
                                    <TextField
                                        label="Instagram URL"
                                        value={draft.social?.instagram}
                                        onChange={(v) =>
                                            patchKey("social", { instagram: v })
                                        }
                                        testId="social-instagram"
                                    />
                                    <TextField
                                        label="TikTok URL"
                                        value={draft.social?.tiktok}
                                        onChange={(v) =>
                                            patchKey("social", { tiktok: v })
                                        }
                                        testId="social-tiktok"
                                    />
                                    <TextField
                                        label="Facebook URL (ixtiyari)"
                                        value={draft.social?.facebook}
                                        onChange={(v) =>
                                            patchKey("social", { facebook: v })
                                        }
                                        testId="social-facebook"
                                    />
                                </div>
                            </Card>
                        </TabsContent>

                        <TabsContent value="hero" className="space-y-6">
                            <Card title="Ana ekran (Hero)">
                                <TextField
                                    label="Üst yazı"
                                    value={draft.hero?.eyebrow}
                                    onChange={(v) => patchKey("hero", { eyebrow: v })}
                                    testId="hero-eyebrow"
                                />
                                <TextAreaField
                                    label="Başlıq (sətirləri Enter ilə ayırın)"
                                    value={draft.hero?.title}
                                    onChange={(v) => patchKey("hero", { title: v })}
                                    rows={3}
                                    testId="hero-title"
                                />
                                <TextAreaField
                                    label="Alt mətn"
                                    value={draft.hero?.subtitle}
                                    onChange={(v) => patchKey("hero", { subtitle: v })}
                                    testId="hero-subtitle"
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <TextField
                                        label="Birinci düymə"
                                        value={draft.hero?.ctaPrimary}
                                        onChange={(v) =>
                                            patchKey("hero", { ctaPrimary: v })
                                        }
                                        testId="hero-cta1"
                                    />
                                    <TextField
                                        label="İkinci düymə (WhatsApp)"
                                        value={draft.hero?.ctaSecondary}
                                        onChange={(v) =>
                                            patchKey("hero", { ctaSecondary: v })
                                        }
                                        testId="hero-cta2"
                                    />
                                </div>
                                <Field label="Typewriter cümlələri (hər sətirdə bir, ingiliscə)">
                                    <textarea
                                        rows={5}
                                        className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
                                        value={(draft.hero?.typewriter || []).join("\n")}
                                        onChange={(e) =>
                                            patchKey("hero", {
                                                typewriter: e.target.value
                                                    .split("\n")
                                                    .filter((x) => x.trim()),
                                            })
                                        }
                                        data-testid="hero-typewriter-list"
                                    />
                                </Field>
                            </Card>
                        </TabsContent>

                        <TabsContent value="pillar" className="space-y-6">
                            <Card title="Psixoloq və fərdi yanaşma">
                                <TextField
                                    label="Üst yazı"
                                    value={draft.pillar?.eyebrow}
                                    onChange={(v) => patchKey("pillar", { eyebrow: v })}
                                    testId="pillar-eyebrow"
                                />
                                <TextAreaField
                                    label="Başlıq"
                                    value={draft.pillar?.title}
                                    onChange={(v) => patchKey("pillar", { title: v })}
                                    rows={2}
                                    testId="pillar-title"
                                />
                                <TextAreaField
                                    label="Mətn"
                                    value={draft.pillar?.body}
                                    onChange={(v) => patchKey("pillar", { body: v })}
                                    rows={5}
                                    testId="pillar-body"
                                />
                                <ListEditor
                                    items={draft.pillar?.steps}
                                    onChange={(v) => patchKey("pillar", { steps: v })}
                                    addLabel="Addım əlavə et"
                                    newItem={() => ({
                                        step: "01",
                                        title: "",
                                        description: "",
                                    })}
                                    testId="pillar-steps"
                                    renderItem={(item, patch, idx) => (
                                        <>
                                            <div className="grid grid-cols-3 gap-3">
                                                <TextField
                                                    label="Addım nömrəsi"
                                                    value={item.step}
                                                    onChange={(v) => patch({ step: v })}
                                                    testId={`pillar-${idx}-step`}
                                                />
                                                <div className="col-span-2">
                                                    <TextField
                                                        label="Başlıq"
                                                        value={item.title}
                                                        onChange={(v) =>
                                                            patch({ title: v })
                                                        }
                                                        testId={`pillar-${idx}-title`}
                                                    />
                                                </div>
                                            </div>
                                            <TextAreaField
                                                label="Təsvir"
                                                value={item.description}
                                                onChange={(v) =>
                                                    patch({ description: v })
                                                }
                                                testId={`pillar-${idx}-desc`}
                                            />
                                        </>
                                    )}
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="courses" className="space-y-6">
                            <Card title="Kurslar">
                                <ListEditor
                                    items={draft.courses}
                                    onChange={(v) => setKey("courses", v)}
                                    addLabel="Kurs əlavə et"
                                    newItem={() => ({
                                        title: "Yeni kurs",
                                        level: "",
                                        description: "",
                                        icon: "BookOpen",
                                    })}
                                    testId="courses-list"
                                    renderItem={(item, patch, idx) => (
                                        <>
                                            <div className="grid grid-cols-2 gap-3">
                                                <TextField
                                                    label="Başlıq"
                                                    value={item.title}
                                                    onChange={(v) => patch({ title: v })}
                                                    testId={`course-${idx}-title`}
                                                />
                                                <TextField
                                                    label="Səviyyə / etiket"
                                                    value={item.level}
                                                    onChange={(v) => patch({ level: v })}
                                                    testId={`course-${idx}-level`}
                                                />
                                            </div>
                                            <TextAreaField
                                                label="Təsvir"
                                                value={item.description}
                                                onChange={(v) =>
                                                    patch({ description: v })
                                                }
                                                testId={`course-${idx}-desc`}
                                            />
                                            <TextField
                                                label="İkon (lucide-react)"
                                                value={item.icon}
                                                onChange={(v) => patch({ icon: v })}
                                                hint="BookOpen, GraduationCap, Languages, Calculator, Globe, Trophy"
                                                testId={`course-${idx}-icon`}
                                            />
                                        </>
                                    )}
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="summer" className="space-y-6">
                            <Card title="Yay proqramı">
                                <TextField
                                    label="Üst yazı"
                                    value={draft.summer?.eyebrow}
                                    onChange={(v) => patchKey("summer", { eyebrow: v })}
                                    testId="summer-eyebrow"
                                />
                                <TextAreaField
                                    label="Başlıq"
                                    value={draft.summer?.title}
                                    onChange={(v) => patchKey("summer", { title: v })}
                                    rows={2}
                                    testId="summer-title"
                                />
                                <TextAreaField
                                    label="Mətn"
                                    value={draft.summer?.body}
                                    onChange={(v) => patchKey("summer", { body: v })}
                                    testId="summer-body"
                                />
                                <Field label="Üstünlüklər (hər sətirdə bir)">
                                    <textarea
                                        rows={5}
                                        className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
                                        value={(draft.summer?.highlights || []).join("\n")}
                                        onChange={(e) =>
                                            patchKey("summer", {
                                                highlights: e.target.value
                                                    .split("\n")
                                                    .filter((x) => x.trim()),
                                            })
                                        }
                                        data-testid="summer-highlights"
                                    />
                                </Field>
                                <ImageField
                                    label="Şəkil"
                                    value={draft.summer?.image}
                                    onChange={(v) => patchKey("summer", { image: v })}
                                    testId="summer-image"
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="teachers" className="space-y-6">
                            <Card title="Müəllimlər">
                                <ListEditor
                                    items={draft.teachers}
                                    onChange={(v) => setKey("teachers", v)}
                                    addLabel="Müəllim əlavə et"
                                    newItem={() => ({
                                        name: "",
                                        role: "",
                                        bio: "",
                                        image: "",
                                    })}
                                    testId="teachers-list"
                                    renderItem={(item, patch, idx) => (
                                        <>
                                            <div className="grid grid-cols-2 gap-3">
                                                <TextField
                                                    label="Ad Soyad"
                                                    value={item.name}
                                                    onChange={(v) => patch({ name: v })}
                                                    testId={`teacher-${idx}-name`}
                                                />
                                                <TextField
                                                    label="Vəzifə"
                                                    value={item.role}
                                                    onChange={(v) => patch({ role: v })}
                                                    testId={`teacher-${idx}-role`}
                                                />
                                            </div>
                                            <TextAreaField
                                                label="Bio"
                                                value={item.bio}
                                                onChange={(v) => patch({ bio: v })}
                                                testId={`teacher-${idx}-bio`}
                                            />
                                            <ImageField
                                                label="Şəkil"
                                                value={item.image}
                                                onChange={(v) => patch({ image: v })}
                                                testId={`teacher-${idx}-image`}
                                            />
                                        </>
                                    )}
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="gallery" className="space-y-6">
                            <Card title="Qalereya">
                                <ListEditor
                                    items={draft.gallery}
                                    onChange={(v) => setKey("gallery", v)}
                                    addLabel="Şəkil əlavə et"
                                    newItem={() => ({ url: "", caption: "" })}
                                    testId="gallery-list"
                                    renderItem={(item, patch, idx) => (
                                        <>
                                            <ImageField
                                                label="Şəkil"
                                                value={item.url}
                                                onChange={(v) => patch({ url: v })}
                                                testId={`gallery-${idx}-image`}
                                            />
                                            <TextField
                                                label="Başlıq"
                                                value={item.caption}
                                                onChange={(v) =>
                                                    patch({ caption: v })
                                                }
                                                testId={`gallery-${idx}-caption`}
                                            />
                                        </>
                                    )}
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="reviews" className="space-y-6">
                            <Card title="Valideyn rəyləri">
                                <TextField
                                    label="Üst yazı"
                                    value={draft.reviews?.eyebrow}
                                    onChange={(v) => patchKey("reviews", { eyebrow: v })}
                                    testId="reviews-eyebrow"
                                />
                                <TextAreaField
                                    label="Başlıq"
                                    value={draft.reviews?.title}
                                    onChange={(v) => patchKey("reviews", { title: v })}
                                    rows={2}
                                    testId="reviews-title"
                                />
                                <ListEditor
                                    items={draft.reviews?.items}
                                    onChange={(v) => patchKey("reviews", { items: v })}
                                    addLabel="Rəy əlavə et"
                                    newItem={() => ({
                                        name: "",
                                        relation: "",
                                        rating: 5,
                                        text: "",
                                    })}
                                    testId="reviews-list"
                                    renderItem={(item, patch, idx) => (
                                        <>
                                            <div className="grid grid-cols-3 gap-3">
                                                <TextField
                                                    label="Ad"
                                                    value={item.name}
                                                    onChange={(v) => patch({ name: v })}
                                                    testId={`review-${idx}-name`}
                                                />
                                                <TextField
                                                    label="Qohumluq / qrup"
                                                    value={item.relation}
                                                    onChange={(v) =>
                                                        patch({ relation: v })
                                                    }
                                                    testId={`review-${idx}-relation`}
                                                />
                                                <TextField
                                                    label="Reyting (1–5)"
                                                    type="number"
                                                    value={item.rating}
                                                    onChange={(v) =>
                                                        patch({ rating: Number(v) })
                                                    }
                                                    testId={`review-${idx}-rating`}
                                                />
                                            </div>
                                            <TextAreaField
                                                label="Mətn"
                                                value={item.text}
                                                onChange={(v) => patch({ text: v })}
                                                rows={3}
                                                testId={`review-${idx}-text`}
                                            />
                                        </>
                                    )}
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="contact" className="space-y-6">
                            <Card title="Əlaqə">
                                <TextField
                                    label="Üst yazı"
                                    value={draft.contact?.eyebrow}
                                    onChange={(v) => patchKey("contact", { eyebrow: v })}
                                    testId="contact-eyebrow"
                                />
                                <TextField
                                    label="Başlıq"
                                    value={draft.contact?.title}
                                    onChange={(v) => patchKey("contact", { title: v })}
                                    testId="contact-title"
                                />
                                <TextAreaField
                                    label="Mətn"
                                    value={draft.contact?.body}
                                    onChange={(v) => patchKey("contact", { body: v })}
                                    testId="contact-body"
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <TextField
                                        label="Telefon"
                                        value={draft.contact?.phone}
                                        onChange={(v) =>
                                            patchKey("contact", { phone: v })
                                        }
                                        testId="contact-phone-field"
                                    />
                                    <TextField
                                        label="E-poçt"
                                        value={draft.contact?.email}
                                        onChange={(v) =>
                                            patchKey("contact", { email: v })
                                        }
                                        testId="contact-email-field"
                                    />
                                    <TextField
                                        label="Ünvan"
                                        value={draft.contact?.address}
                                        onChange={(v) =>
                                            patchKey("contact", { address: v })
                                        }
                                        testId="contact-address-field"
                                    />
                                    <TextField
                                        label="İş saatları"
                                        value={draft.contact?.workingHours}
                                        onChange={(v) =>
                                            patchKey("contact", { workingHours: v })
                                        }
                                        testId="contact-hours-field"
                                    />
                                </div>
                            </Card>
                        </TabsContent>

                        <TabsContent value="seo" className="space-y-6">
                            <Card title="SEO tənzimləmələri">
                                <TextField
                                    label="Sayt başlığı"
                                    value={draft.seo?.title}
                                    onChange={(v) => patchKey("seo", { title: v })}
                                    testId="seo-title"
                                />
                                <TextAreaField
                                    label="Açıqlama (max 160 simvol)"
                                    value={draft.seo?.description}
                                    onChange={(v) =>
                                        patchKey("seo", { description: v })
                                    }
                                    rows={3}
                                    testId="seo-description"
                                />
                                <TextAreaField
                                    label="Açar sözlər (vergüllə ayrılmış)"
                                    value={draft.seo?.keywords}
                                    onChange={(v) => patchKey("seo", { keywords: v })}
                                    rows={3}
                                    testId="seo-keywords"
                                />
                            </Card>
                        </TabsContent>

                        <TabsContent value="settings" className="space-y-6">
                            <SettingsPanel />
                        </TabsContent>
                    </div>
                </Tabs>
            </main>
        </div>
    );
}

function SettingsPanel() {
    const [fb, setFb] = useState(
        () =>
            getFirebaseConfig() || {
                apiKey: "",
                authDomain: "",
                projectId: "",
                storageBucket: "",
                messagingSenderId: "",
                appId: "",
            }
    );
    const [r2, setR2] = useState(
        () =>
            getR2Config() || {
                accountId: "",
                accessKeyId: "",
                secretAccessKey: "",
                bucket: "",
                publicBaseUrl: "",
            }
    );
    const [newPwd, setNewPwd] = useState("");

    const saveFb = () => {
        if (!fb.apiKey || !fb.projectId) {
            toast.error("apiKey və projectId mütləqdir");
            return;
        }
        saveFirebaseConfig(fb);
        toast.success(
            "Firebase konfiqurasiyası saxlandı. Səhifəni yenidən yükləyin."
        );
    };
    const clearFb = () => {
        clearFirebaseConfig();
        setFb({
            apiKey: "",
            authDomain: "",
            projectId: "",
            storageBucket: "",
            messagingSenderId: "",
            appId: "",
        });
        toast.success("Firebase təmizləndi");
    };
    const saveR2c = () => {
        if (
            !r2.accountId ||
            !r2.accessKeyId ||
            !r2.secretAccessKey ||
            !r2.bucket
        ) {
            toast.error(
                "Bütün R2 sahələri mütləqdir (publicBaseUrl ixtiyaridir)"
            );
            return;
        }
        saveR2Config(r2);
        toast.success("Cloudflare R2 konfiqurasiyası saxlandı");
    };
    const changePwd = async () => {
        if (newPwd.length < 4) {
            toast.error("Şifrə ən azı 4 simvol olmalıdır");
            return;
        }
        await setAdminPwd(newPwd);
        setNewPwd("");
        toast.success("Şifrə dəyişdirildi");
    };

    return (
        <>
            <Card title="Firebase konfiqurasiyası">
                <p className="text-xs text-slate-500 leading-relaxed">
                    Firebase konsoluna girin → Project Settings → Your apps → Web app →
                    SDK setup and configuration → Config. Açarları buraya kopyalayın.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        ["apiKey", "API Key"],
                        ["authDomain", "Auth Domain"],
                        ["projectId", "Project ID"],
                        ["storageBucket", "Storage Bucket"],
                        ["messagingSenderId", "Messaging Sender ID"],
                        ["appId", "App ID"],
                    ].map(([k, lbl]) => (
                        <div key={k}>
                            <Label className="text-xs tracking-[0.18em] uppercase text-slate-600">
                                {lbl}
                            </Label>
                            <Input
                                value={fb[k] || ""}
                                onChange={(e) =>
                                    setFb({ ...fb, [k]: e.target.value })
                                }
                                className="bg-white mt-1"
                                data-testid={`fb-${k}`}
                            />
                        </div>
                    ))}
                </div>
                <div className="flex gap-2 pt-2">
                    <Button
                        onClick={saveFb}
                        className="bg-[#0A1F3D] hover:bg-[#061632] text-white"
                        data-testid="fb-save"
                    >
                        <Save size={14} className="mr-2" />
                        Saxla
                    </Button>
                    <Button variant="outline" onClick={clearFb} data-testid="fb-clear">
                        Təmizlə
                    </Button>
                </div>
            </Card>

            <Card title="Cloudflare R2 konfiqurasiyası">
                <p className="text-xs text-slate-500 leading-relaxed">
                    Cloudflare Dashboard → R2 → Manage API tokens. Account ID dashboard
                    ana səhifəsində mövcuddur. Bucket public access enabled olmalıdır.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        ["accountId", "Account ID"],
                        ["bucket", "Bucket name"],
                        ["accessKeyId", "Access Key ID"],
                        ["secretAccessKey", "Secret Access Key"],
                        ["publicBaseUrl", "Public Base URL (ixtiyari)"],
                    ].map(([k, lbl]) => (
                        <div key={k}>
                            <Label className="text-xs tracking-[0.18em] uppercase text-slate-600">
                                {lbl}
                            </Label>
                            <Input
                                value={r2[k] || ""}
                                onChange={(e) =>
                                    setR2({ ...r2, [k]: e.target.value })
                                }
                                className="bg-white mt-1"
                                type={k === "secretAccessKey" ? "password" : "text"}
                                data-testid={`r2-${k}`}
                            />
                        </div>
                    ))}
                </div>
                <Button
                    onClick={saveR2c}
                    className="bg-[#0A1F3D] hover:bg-[#061632] text-white"
                    data-testid="r2-save"
                >
                    <Save size={14} className="mr-2" />
                    Saxla
                </Button>
            </Card>

            <Card title="Admin şifrəsini dəyişdir">
                <Label className="text-xs tracking-[0.18em] uppercase text-slate-600">
                    Yeni şifrə
                </Label>
                <Input
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    className="bg-white"
                    data-testid="admin-new-password"
                />
                <Button
                    onClick={changePwd}
                    className="bg-[#0A1F3D] hover:bg-[#061632] text-white"
                    data-testid="admin-change-password"
                >
                    <KeyRound size={14} className="mr-2" />
                    Şifrəni dəyişdir
                </Button>
            </Card>
        </>
    );
}
